import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../../components/feature/Footer';
import TableOfContents from '../../../components/feature/TableOfContents';
import ArticlePartOne from './components/ArticlePartOne';
import ArticlePartTwo from './components/ArticlePartTwo';
import ArticlePartThree from './components/ArticlePartThree';
import ArticlePartFour from './components/ArticlePartFour';

const relatedArticles = [
  {
    to: '/get-cash/sell-house-as-is-el-paso',
    icon: 'ri-home-2-line',
    tag: 'Selling Tips',
    title: 'Sell Your El Paso House As-Is for Cash',
    description: 'Skip repairs, skip showings. Learn how El Paso homeowners sell in any condition and still walk away with a fair cash offer.',
    readTime: '12 min read',
  },
  {
    to: '/get-cash/fire-damaged-property-el-paso',
    icon: 'ri-fire-line',
    tag: 'Distressed Property',
    title: 'Selling a Fire-Damaged Property in El Paso',
    description: 'Fire damage doesn\'t have to mean a dead-end sale. Discover your options for selling a fire-damaged home quickly and without costly repairs.',
    readTime: '10 min read',
  },
  {
    to: '/get-cash/hidden-costs-foreclosure-el-paso',
    icon: 'ri-bank-line',
    tag: 'Foreclosure',
    title: 'Hidden Costs of Foreclosure in El Paso',
    description: 'Foreclosure is more expensive than most homeowners realize. See the full financial picture before deciding how to handle a missed mortgage.',
    readTime: '11 min read',
  },
  {
    to: '/quick-sale/cash-sale-quick-close',
    icon: 'ri-calendar-check-line',
    tag: 'Quick Sale',
    title: 'What a Cash Sale & Quick Close Looks Like',
    description: 'A step-by-step walkthrough of the entire cash sale process in El Paso — from first call to keys handed over in as little as 7 days.',
    readTime: '9 min read',
  },
  {
    to: '/get-cash/sell-house-code-violations-el-paso',
    icon: 'ri-error-warning-line',
    tag: 'Code Violations',
    title: 'Sell a House With Code Violations in El Paso',
    description: 'Unpermitted work, failed inspections, city citations — none of these have to block your sale. Here\'s how to move forward fast.',
    readTime: '10 min read',
  },
  {
    to: '/get-cash/inherit-house-texas',
    icon: 'ri-file-paper-2-line',
    tag: 'Inherited Property',
    title: 'Inherited a House in Texas? Here\'s What to Do',
    description: 'Probate, taxes, co-heirs, and condition issues — get a clear guide on your options when you\'ve inherited a Texas property.',
    readTime: '13 min read',
  },
];

export default function SellHouseAnimalHoardingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    { id: 'understanding-animal-hoarding', title: 'Understanding Animal Hoarding Impact' },
    { id: 'health-hazards', title: 'Health and Safety Hazards' },
    { id: 'structural-damage', title: 'Structural Damage Assessment' },
    { id: 'excessive-pet-waste', title: 'Excessive Pet Waste Problems' },
    { id: 'cat-urine-damage', title: 'Cat Urine Specific Damage' },
    { id: 'why-traditional-fails', title: 'Why Traditional Sales Fail' },
    { id: 'sell-as-is-cash', title: 'Sell As-Is for Quick Cash' },
    { id: 'non-judgmental', title: 'Non-Judgmental Approach' },
    { id: 'cash-sale-process', title: 'Cash Sale Process' },
    { id: 'what-to-leave', title: 'What You Can Leave Behind' },
    { id: 'calculating-net-proceeds', title: 'Calculating Net Proceeds' },
    { id: 'faq', title: 'Frequently Asked Questions' },
    { id: 'moving-forward', title: 'Moving Forward' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-black text-white sticky top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <i className="ri-home-4-fill text-2xl"></i>
              <span className="font-bold text-xl">El Paso Cash Home Buyers</span>
            </Link>
            <a 
              href="tel:915-745-7883" 
              className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-fill"></i>
              915-745-7883
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-300 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/quick-sale" className="hover:text-white transition-colors">Quick Sale</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Sell House With Previous Animal Hoarding</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Sell a House With Previous Animal Hoarding: Get Cash As-Is, No Judgment
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl">
            Compassionate cash buyers purchase homes affected by animal hoarding in any condition. No cleaning required, no remediation needed, no judgment—just a fair offer and a quick close.
          </p>
          <div className="flex flex-wrap gap-4 items-center">
            <a 
              href="tel:915-745-7883" 
              className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 rounded-lg font-bold text-lg transition-colors whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-phone-fill"></i>
              Call 915-745-7883
            </a>
            <div className="flex items-center gap-2 text-gray-300">
              <i className="ri-time-line"></i>
              <span>18 min read</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300">
              <i className="ri-calendar-line"></i>
              <span>Close in 7-30 days</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          {/* Sidebar - Table of Contents */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24">
              <TableOfContents sections={sections} />
            </div>
          </aside>

          {/* Article Content */}
          <article className="lg:col-span-3 prose prose-lg max-w-none">
            <ArticlePartOne />
            <ArticlePartTwo />
            <ArticlePartThree />
            <ArticlePartFour />
          </article>
        </div>
      </div>

      {/* Related Articles */}
      <section className="bg-gray-50 border-t border-gray-200 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <span className="text-amber-500 font-semibold text-sm uppercase tracking-widest">Keep Reading</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Related Articles</h2>
            <p className="text-gray-500 mt-2 text-base">More guides to help El Paso homeowners sell with confidence.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedArticles.map((article) => (
              <Link
                key={article.to}
                to={article.to}
                className="group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col overflow-hidden cursor-pointer"
              >
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg text-amber-500 group-hover:bg-amber-500 group-hover:text-white transition-colors duration-200">
                      <i className={`${article.icon} text-xl`}></i>
                    </div>
                    <span className="text-xs font-semibold text-amber-600 uppercase tracking-wide bg-amber-50 px-2 py-1 rounded-full whitespace-nowrap">
                      {article.tag}
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors duration-200 leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed flex-1">
                    {article.description}
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.readTime}
                    </span>
                    <span className="text-amber-500 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all duration-200 whitespace-nowrap">
                      Read more <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}